import React, { useEffect, useState } from 'react';
import './PokerTableDemo.css';
import { t } from '../i18n';
import { formatCurrency } from '../utils/currency';

// Two equal phones beside community cards. One accurate heads-up flop:
// pot already $4 from blinds, Jony bets $2, Alex calls $2, freeze.
// Chips conserved ($50 + $50). Not a GameBoard clone.

const SUIT = {
  h: { g: '♥', c: 'red' },
  d: { g: '♦', c: 'red' },
  c: { g: '♣', c: 'blk' },
  s: { g: '♠', c: 'blk' },
};

const FLOP = [
  { r: '7', s: 'c' },
  { r: '9', s: 'd' },
  { r: 'K', s: 's' },
];

const SEATS = [
  { id: 0, name: 'Jony', seat: 'left' },
  { id: 1, name: 'Alex', seat: 'right' },
];

const BUY_IN = 5000;
const START_POT = 400; // SB $1 + BB $2 + SB call $1
const START_STACK = BUY_IN - 200; // $48 each after matching $2
const BET = 200;

function money(cents) {
  return formatCurrency(cents).replace(/\.00$/, '');
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function fresh() {
  return {
    turn: 0,
    pressing: false,
    pot: START_POT,
    stacks: [START_STACK, START_STACK],
    flashId: null,
    paused: false,
  };
}

function finished() {
  return {
    turn: null,
    pressing: false,
    pot: START_POT + BET + BET,
    stacks: [START_STACK - BET, START_STACK - BET],
    flashId: null,
    paused: true,
  };
}

export default function PokerTableDemo({ className = '' }) {
  const [g, setG] = useState(fresh);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setG(finished());
      return undefined;
    }

    let cancelled = false;
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));

    async function playOnce() {
      let s = fresh();
      setG({ ...s, stacks: [...s.stacks] });
      await wait(800);
      if (cancelled) return;

      // Jony bets $2 (first to act on the flop).
      s = { ...s, turn: 0, pressing: false, flashId: null };
      setG({ ...s, stacks: [...s.stacks] });
      await wait(420);
      if (cancelled) return;

      s = { ...s, pressing: true };
      setG({ ...s, stacks: [...s.stacks] });
      await wait(450);
      if (cancelled) return;

      s = {
        ...s,
        pressing: false,
        pot: s.pot + BET,
        stacks: [s.stacks[0] - BET, s.stacks[1]],
        flashId: 0,
      };
      setG({ ...s, stacks: [...s.stacks] });
      await wait(520);
      if (cancelled) return;

      // Alex calls $2.
      s = { ...s, turn: 1, pressing: false, flashId: null };
      setG({ ...s, stacks: [...s.stacks] });
      await wait(420);
      if (cancelled) return;

      s = { ...s, pressing: true };
      setG({ ...s, stacks: [...s.stacks] });
      await wait(450);
      if (cancelled) return;

      s = {
        ...s,
        pressing: false,
        pot: s.pot + BET,
        stacks: [s.stacks[0], s.stacks[1] - BET],
        flashId: 1,
      };
      setG({ ...s, stacks: [...s.stacks] });
      await wait(520);
      if (cancelled) return;

      s = { ...s, turn: null, pressing: false, flashId: null, paused: true };
      setG({ ...s, stacks: [...s.stacks] });
      await wait(2600);
    }

    (async function loop() {
      while (!cancelled) {
        await playOnce();
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return (
    <div className={className}>
      <p className="sr-only">
        Two players with real hole cards and three community cards.
        Jony bets two dollars on the flop. Alex calls. Stacks and the pot update on their phones.
      </p>
      <div className="poker-demo" aria-hidden="true" dir="ltr">
        <div className="pd-table">
          <Seat
            player={SEATS[0]}
            stack={g.stacks[0]}
            isTurn={g.turn === 0}
            pressing={g.turn === 0 && g.pressing}
            flash={g.flashId === 0}
            actLabel={t('demo.bet', { amount: money(BET) })}
          />
          <div className="pd-board">
            <div className="pd-board-cards">
              {FLOP.map((card, i) => (
                <Card key={i} card={card} faceUp variant="board" />
              ))}
            </div>
            <div className={`pd-pot tnum ${g.flashId !== null ? 'pd-flash' : ''}`}>
              {t('poker.pot')} {money(g.pot)}
            </div>
            <div className="pd-board-label">{t('demo.caption')}</div>
          </div>
          <Seat
            player={SEATS[1]}
            stack={g.stacks[1]}
            isTurn={g.turn === 1}
            pressing={g.turn === 1 && g.pressing}
            flash={g.flashId === 1}
            actLabel={t('demo.call', { amount: money(BET) })}
          />
        </div>
      </div>
    </div>
  );
}

function Seat({ player, stack, isTurn, pressing, flash, actLabel }) {
  return (
    <div className={`pd-seat pd-${player.seat}`}>
      <MiniPhone
        name={player.name}
        stack={stack}
        isTurn={isTurn}
        pressing={pressing}
        flash={flash}
        actLabel={actLabel}
      />
      <HoleCards />
    </div>
  );
}

function HoleCards() {
  return (
    <div className="pd-hole">
      <Card faceUp={false} variant="hole" />
      <Card faceUp={false} variant="hole" />
    </div>
  );
}

function Card({ card, faceUp, variant }) {
  const s = card ? SUIT[card.s] : null;
  return (
    <div className={`pd-card pd-card-${variant} ${faceUp ? 'up' : ''}`}>
      <div className="pd-card-inner">
        <div className="pd-back" />
        <div className={`pd-face ${s ? s.c : 'blk'}`}>
          {card && (
            <>
              <span className="pd-idx pd-idx-tl">
                <span className="pd-rank">{card.r}</span>
                <span className="pd-suit">{s.g}</span>
              </span>
              <span className="pd-pip">{s.g}</span>
              <span className="pd-idx pd-idx-br">
                <span className="pd-rank">{card.r}</span>
                <span className="pd-suit">{s.g}</span>
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniPhone({ name, stack, isTurn, pressing, flash, actLabel }) {
  return (
    <div className={`pd-phone ${isTurn ? 'is-turn' : ''} ${pressing ? 'is-pressing' : ''}`}>
      <div className="pd-bezel">
        <div className="pd-screen">
          <div className="pd-name">{name}</div>
          <div className={`pd-stack tnum ${flash ? 'pd-flash' : ''}`}>{money(stack)}</div>
          <div className="pd-actions">
            <div className="pd-btn">{t('demo.fold')}</div>
            <div className={`pd-btn pd-btn-call ${pressing ? 'is-active' : ''}`}>{actLabel}</div>
            <div className="pd-btn">{t('demo.raise')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
